import React, { useState, useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import InputNumberNoScroll from "../../../components/inputs/inputNumberNoScroll";


// Imports de Componentes
import BtnGuardar from "../../../components/buttons/BtnGuardar";
import BtnCancelar from "../../../components/buttons/BtnCancelar";

// Imports de Autocomplete
import CertificadoAutocomplete from "../../../components/autocomplete/CertificadoAutocomplete";
import ProductoAutocomplete from "../../../components/autocomplete/ProductoAutocomplete";

// Imports de Utilidades y API
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson, notify } from "../../../utils/http";
// Asumimos que tienes esta utilidad, igual que en VerRemito
import { formatoPy } from "../../../utils/formatoNumeros"; 

export default function CrearProdCertificado({
  onClose = () => {},
  onCreated = () => {},
  embedded = false,
  certificadoPredefinido = null,
}) {
  
  // --- ESTADOS ---
  const [certificadoObj, setCertificadoObj] = useState(certificadoPredefinido || null);

  useEffect(() => {
    setCertificadoObj(certificadoPredefinido || null);
  }, [certificadoPredefinido]);

  const [productoObj, setProductoObj] = useState(null); 
  const [qnProducto, setQnProducto] = useState("");
  const [valorMontoDescuento, setValorMontoDescuento] = useState("");
  const [qnUtilizado] = useState("0");
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // NUEVO ESTADO: Lista para mostrar en la tabla inferior
  const [listaAgregados, setListaAgregados] = useState([]);

  const isValid =
    certificadoObj !== null &&
    productoObj !== null &&
    qnProducto !== "" &&
    valorMontoDescuento !== "";

  useEffect(() => {
    if (embedded) return; 
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, embedded]);

  // ============================================================
  // NUEVO: Cargar historial de productos existentes del certificado
  // ============================================================
  useEffect(() => {
    if (certificadoPredefinido?.id) {
      fetchExistingDetails(certificadoPredefinido.id);
    }
  }, [certificadoPredefinido]);

  const fetchExistingDetails = async (certId) => {
    try {
        // Usamos la ruta de listado filtrando por certificadoId
        // Ajustamos la URL base según tu estructura apiRoutes
        let baseUrl = "";
        if (apiRoutes.certificadoDetalles.listUrl) {
             baseUrl = apiRoutes.certificadoDetalles.listUrl({});
        } else {
             // Fallback si listUrl no es funcion
             baseUrl = apiRoutes.certificadoDetalles.list || "/certificadoDetalles/list";
        }

        // Limpiamos si viene con query params extraños
        if (baseUrl.includes("?")) baseUrl = baseUrl.split("?")[0];

        // Traemos los últimos 50 registros para mostrar contexto
        const url = `${baseUrl}?certificadoId=${certId}&page=0&size=50`;
        
        const res = await appFetchJson(url);
        // Soportamos paginación estándar de Spring (content o items)
        const items = res.items || res.content || [];
        
        setListaAgregados(items);
    } catch (e) {
        console.error("Error cargando historial de productos:", e);
    }
  };

const guardar = async () => {
    if (!isValid || saving) return;
    setSaving(true);
    setError("");

    try {
      const payload = {
        certificadoId: certificadoObj.id,
        productoId: productoObj.id, 
        qnProducto: Number(qnProducto),
        valorMontoDescuento: Number(valorMontoDescuento),
        qnUtilizado: Number(qnUtilizado),
        codigoAnio: 0,
      };

      const response = await appFetchJson(
          apiRoutes.certificadoDetalles.create, 
          {
           method: "POST",
           headers: { "Content-Type": "application/json" },
           body: JSON.stringify(payload),
          }
      );

      // 2. VALIDACIÓN MANUAL
      // Si por alguna razón el backend devuelve 200 OK pero manda un objeto error
      // (no un array), forzamos el modal global manualmente usando notify().
      if (!Array.isArray(response)) {
          const msgError = response.message || response.error_description || "El producto ya está asociado o hubo un error.";
          
          // --- AQUÍ DISPARAMOS EL MODAL GENÉRICO MANUALMENTE ---
          notify({ 
            type: "error", 
            message: msgError,
            payload: response 
          });

          throw new Error(msgError);
      }

      // Éxito
      const createdList = response;
      onCreated(createdList);

      setListaAgregados((prev) => [...createdList, ...prev]);

      if (embedded) {
        setProductoObj(null);
        setQnProducto("");
        setValorMontoDescuento("");
      } else {
        onClose();
      }

    } catch (e) {
      console.error("Error controlado:", e);
    } finally {
      setSaving(false);
    }
  };

  // Helper para formato simple si no importaste formatoPy
  const fmtMoney = (val) => val ? Number(val).toLocaleString("es-PY") : "0";

  // === RENDERIZADO ===
  const content = ( 
    <>
    {/* SOLUCIÓN CSS: Forzamos ancho 100% solo en esta vista */}
      <style>{`
        .fix-autocomplete-width .relative { width: 100%; }
        .fix-autocomplete-width input { width: 100% !important; }
      `}</style>

      {/* HEADER */}
      {!embedded && (
        <div className="modal-header flex justify-between items-center">
          <h2 className="section-title">Asignar Producto a Certificado</h2>
          <button className="modal-close-x" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
      )}

      {/* BODY FORMULARIO */}
      <div className={embedded ? "" : "modal-body"}>
        {!embedded && (
          <p className="text-gray-600 mb-4 italic">
            Complete los datos para asignar un producto.
          </p>
        )}

        <div className="form-grid">
          
          {/* 1. CERTIFICADO */}
          <div className="form-row col-span-2">
            <label className="form-label">Impuesto al Valor Selectivo (Certificados)*:</label>
            {certificadoPredefinido ? (
              <input 
                type="text" 
                className="input bg-gray-100 text-gray-700 font-semibold cursor-not-allowed"
                value={certificadoPredefinido.numero || "Sin Numero"}
                readOnly
                disabled
              />
            ) : (
              <CertificadoAutocomplete
                value={certificadoObj}
                onChange={(obj) => setCertificadoObj(obj)}
                placeholder="Buscar por Nro de Certificado..."
              />
            )}
          </div>

          {/* 2. PRODUCTO */}
          <div className="form-row col-span-2 fix-autocomplete-width">
            <label className="form-label">Producto *</label>
            <ProductoAutocomplete
              value={productoObj}      
              onChange={setProductoObj} 
              disabled={saving}
              placeholder="Escriba código o nombre del producto..."
            />
          </div>

         {/* 3. CANTIDAD */}
          <div className="form-row col-span-2">
            <label className="form-label">Cant. del Producto (para c/u) *</label>
            <InputNumberNoScroll
              className="input w-full h-[38px] bg-white border rounded px-3"
              value={qnProducto}
              onChange={(e) => setQnProducto(e.target.value)}
              placeholder="0"
            />
          </div>

          {/* 4. MONTO DESCUENTO */}
          <div className="form-row col-span-2">
            <label className="form-label">Monto del descuento (para c/u) *</label>
            <InputNumberNoScroll
              className="input w-full h-[38px] bg-white border rounded px-3"
              value={valorMontoDescuento}
              onChange={(e) => setValorMontoDescuento(e.target.value)}
              placeholder="0"
            />
          </div>

          {/* 5. UTILIZADO */}
          <div className="form-row col-span-2">
            <label className="form-label">Utilizado hasta el momento: (Inicial)</label>
            <input
              type="number"
              className="input w-1/2 bg-gray-200 text-gray-500 cursor-not-allowed"
              value={qnUtilizado}
              readOnly
              placeholder="0"
            />
          </div>
        </div>
      </div>

      {/* FOOTER BOTONES */}
      <div className={embedded ? "mt-6 border-b pb-6" : "modal-footer"}>
        <div className="btn-row">
          <BtnGuardar
            label={embedded ? "Guardar y Agregar Otro" : "Guardar"} 
            isValid={isValid}
            saving={saving}
            onClick={guardar}
          />
          {!embedded && <BtnCancelar onClick={onClose} />}
        </div>
      </div>

      {/* === NUEVA SECCIÓN: TABLA DE DETALLES AGREGADOS === */}
      {embedded && listaAgregados.length > 0 && (
        <div className="mt-6 animate-fadeIn">
            <h3 className="text-sm font-bold text-gray-700 mb-2 pl-2 border-l-4 border-green-600">
                Productos Agregados Recientemente
            </h3>
            
            <div className="overflow-x-auto border rounded-lg shadow-sm">
                <table className="table-card table-xs w-full">
                    <thead className="bg-green-700 text-white">
                        <tr>
                            <th className="px-3 py-2 text-left">Producto</th>
                            <th className="px-3 py-2 text-right">Cant.</th>
                            <th className="px-3 py-2 text-right">Monto Descuento</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {listaAgregados.map((item, idx) => (
                            <tr key={item.id || idx} className="hover:bg-gray-50">
                                <td className="px-3 py-2 font-medium text-gray-700">
                                    {/* Ajusta según tu DTO: item.productoNombre o item.producto?.nombre */}
                                    {item.productoNombre || item.producto?.nombre || "Producto sin nombre"}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    {fmtMoney(item.qnProducto)}
                                </td>
                                <td className="px-3 py-2 text-right text-green-700 font-semibold">
                                    {fmtMoney(item.valorMontoDescuento)} Gs.
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="text-right text-xs text-gray-400 mt-1">
               Mostrando {listaAgregados.length} registro(s) en esta sesión.
            </div>
        </div>
      )}
    </>
  );

  if (embedded) {
    return <div className="bg-white p-1">{content}</div>;
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
        {content}
      </div>
    </div>
  );
}